import { Accidental, Formatter, Modifier, RenderContext, Stem } from 'vexflow4';
import { StaveNote } from '../common/extension/MusiconStaveNote';
import Stave from '../common/extension/MusiconStave';
import { Interval, Note, distance, transpose } from 'tonal';
import { accNotesType } from "./types";

export interface StaveOption {
    clef?: string;
    width: number;
    x: number;
    y: number;
}

export interface KeyRange {
    max: string;
    min: string;
}

export const getTouchPositionInCanvas = (canvas: any, evt: Position): Position => {
    //pegando apenas os valores de border e padding (eliminando coisas como cor e caracteres de pixel 'px'
    // var border = getStyle(canvas.id, 'border-left-width');
    // var borderValue = border!=null?border.substring(0, border.indexOf(border.match(/\D/))):0;
    const borderValue = 0;
    // var padding = getStyle(canvas.id, 'padding-left');
    // var paddingValue = padding!=null?padding.substring(0, padding.indexOf(padding.match(/\D/))):0;
    const paddingValue = 0;
    const rect = canvas.getBoundingClientRect();

    return {
        x: evt.x - rect.left - borderValue - paddingValue,
        y: evt.y - rect.top - borderValue - paddingValue,
    };
};

export interface Position {
    x: number;
    y: number;
}

export const findWhichStaveMouseOver = (staveList: Stave[], mousePos: Position) => {
    for (var i = 0; i < staveList.length; i++) {
        var box = staveList[i].getBoundingBox();
        if (isCursorWithinRectangle(box.x, box.y, box.w, box.h, mousePos.x, mousePos.y)) return staveList[i];
    }
    return null;
};

const isCursorWithinRectangle = (x: number, y: number, width: number, height: number, mouseX: number, mouseY: number) => {
    if (mouseX > x && mouseX < x + width && mouseY > y && mouseY < y + height) {
        return true;
    }
    return false;
};

export const addNote = (stave: Stave, context: RenderContext, callback?: (notes: StaveNote[]) => void) => {
    const notes = [
        { keys: ['C/4'], duration: '4' },
        { keys: ['D/4'], duration: '4' },
        { keys: ['E/4'], duration: '4' },
        { keys: ['F/4'], duration: '4' },
    ]
        .map(
            ({ keys, duration }) =>
                new StaveNote({
                    keys,
                    duration,
                }),
        )
        .map((note) => note.setStave(stave).setContext(context));

    callback?.call(this, notes);

    Formatter.FormatAndDraw(context, stave, notes);
};

export const getStemDirection = (stave: Stave, y: number) => {
    var reference = stave.getYForLine(2);

    if (y < reference) return StaveNote.STEM_DOWN;
    else return StaveNote.STEM_UP;
};

export const deepClone = (obj: any) => {
    let objProto = Object.getPrototypeOf(obj);
    // @ts-ignore
    return Object.assign(Object.create(objProto), obj);
};

/**
 *
 * @param obj1 The first object
 * @param obj2 The second object
 * @returns A new object representing the merged objects. If both objects passed as param have the same prop, then obj2 property is returned.
 */
export const mergeProperties = (obj1: any, obj2: any) => {
    return Object.assign({}, obj1, obj2);
};

export const countDuration = (duration: string): number => {
    const durationMap = new Map();
    durationMap.set('1', 1);
    durationMap.set('2', 2);
    durationMap.set('4', 4);
    durationMap.set('8', 8);
    durationMap.set('16', 16);
    durationMap.set('32', 32);
    durationMap.set('64', 64);
    durationMap.set('w', 1);
    durationMap.set('h', 2);
    durationMap.set('q', 4);

    return durationMap.get(duration);
};

export const formatStavesToJson = (staves: Stave[]) => {
    return staves
        .filter((stave: Stave) => {
            return stave.getTickables().length > 0;
        })
        .map((stave: Stave) => {
            return {
                notes: stave.getTickables().map((note: StaveNote) => {
                    const accidentals = getAccidentals(note);

                    const direction = (note.multipleStems ?? []).map((d) => {
                        if (d === Stem.UP) {
                            return 'up';
                        } else {
                            return 'down';
                        }
                    });

                    const durations = (note.multipleDurations ?? []).map((d) => {
                        return d;
                    });

                    return {
                        keys: note.getKeys(),
                        duration: note.getDuration(),
                        accidentals,
                        direction,
                        durations,
                    };
                }),
            };
        });
};

export const getAccidentals = (note: StaveNote): any[] => {
    return note
        .getModifiers()
        .filter((modifier: Modifier) => {
            return modifier instanceof Accidental;
        })
        .map((modifier: Modifier) => {
            const index = modifier.getIndex();

            if (modifier instanceof Accidental) {
                return { modifier: modifier.type, index };
            }

            return null;
        });
};

export const groupSequence = (notes: StaveNote[] = [], limitDuration: boolean = false): StaveNote[][] => {
    const sequence = notes.map((note: StaveNote) => 1 / countDuration(note.getDuration()));
    const groups: number[][] = [];
    let currentGroup: number[] = [];

    const noteGroups: StaveNote[][] = [];
    let currentNoteGroup: StaveNote[] = [];
    let preDuration = countDuration(notes[0]?.getDuration());

    for (let i = 0; i < sequence.length; i++) {
        let shouldSplitStave = false;

        if (limitDuration) {
            shouldSplitStave =
                currentGroup.reduce((sum, num) => sum + num, 0) + sequence[i] > 1 || preDuration !== countDuration(notes[i].getDuration());
        } else {
            shouldSplitStave = currentGroup.reduce((sum, num) => sum + num, 0) >= 1;
        }

        if (shouldSplitStave) {
            groups.push(currentGroup);
            noteGroups.push(currentNoteGroup);
            currentGroup = [sequence[i]];
            currentNoteGroup = [notes[i]];
        } else {
            currentGroup.push(sequence[i]);
            currentNoteGroup.push(notes[i]);
        }
        preDuration = countDuration(notes[i].getDuration());
    }

    if (currentGroup.length > 0) {
        groups.push(currentGroup);
        noteGroups.push(currentNoteGroup);
    }

    return noteGroups;
};

export const transposePitch = (pitch: string, direction: string = 'up', range: KeyRange = {
    max: 'G/6',
    min: 'B/2'
}): string => {
    const tonalPitch = convertNoteFormat(pitch);

    let dst = '0';
    if (direction === 'up') {
        dst = pitch.includes('E') || pitch.includes('B') ? '2m' : '2M';
    } else {
        dst = pitch.includes('F') || pitch.includes('C') ? '-2m' : '-2M';
    }

    let newKey = pitch;
    const newPitch = Note.transpose(tonalPitch, dst);

    if (newKey !== '' && isInRange(newPitch, range)) {
        newKey = convertNoteFormat(newPitch);
    }

    return newKey;
};

const isInRange = (newPitch: string, range: { max: string; min: string }) => {
    let semMax = Interval.semitones(distance(newPitch, convertNoteFormat(range.max)));
    let semMin = Interval.semitones(distance(convertNoteFormat(range.min), newPitch));

    semMax = semMax ? semMax : 0;
    semMin = semMin ? semMin : 0;

    return semMax >= 0 && semMin >= 0;
};

// TypeScript
export const convertNoteFormat = (note: string): string => {
    if (note.includes('/')) {
        // if input is in C/4 format
        let [noteLetter, noteOctave] = note.split('/');
        return `${noteLetter}${noteOctave}`; // convert to C4 format
    } else {
        // assume input is in C4 format
        let noteLetter = note.slice(0, -1);
        let noteOctave = note.slice(-1);
        return `${noteLetter}/${noteOctave}`; // Convert to C/4 format
    }
};

export const getMoveKeysFunc = (action: string, currentNote: StaveNote | null, range: KeyRange) => {
    return (stave: Stave) => {
        if (!checkCurrentStave(stave, currentNote)) {
            return stave;
        }

        const notes = stave.getTickables();
        const newNotes = notes.map((note: StaveNote) => {
            if (!checkCurrentNote(note, currentNote)) {
                return note;
            }

            const newKeys = note.getKeys().map((key: string) => {
                let newKey = key;
                try {
                    newKey = transposePitch(key, action, range);
                } catch (e) {
                    console.log(e);
                }
                return newKey;
            });

            note.keys = uniqueStringArray(newKeys);

            const newNote = new StaveNote({
                keys: newKeys,
                duration: note.getDuration(),
            });

            const stave = note.getStave();

            if (stave) newNote.setStave(stave);

            newNote.autoStem();
            getAccidentals(note).forEach((accidental) => {
                newNote.addModifier(new Accidental(accidental?.modifier || accidental));
            });

            return newNote;
        });

        stave.setTickables(newNotes);

        return stave;
    };
};

export const checkCurrentStave = (stave: Stave, currentNote: StaveNote | null): boolean => {
    if (!currentNote) {
        return true;
    }

    const curStaveBox = currentNote.getStave()?.getBoundingBox();
    const staveBox = stave.getBoundingBox();

    try {
        if (curStaveBox?.getX() !== staveBox.getX() || curStaveBox?.getY() !== staveBox.getY()) {
            return false;
        }
    } catch (e) {
        console.log(e);
        return false;
    }

    return true;
};

export const checkCurrentNote = (note: StaveNote, currentNote: StaveNote | null): boolean => {
    if (!currentNote) {
        return true;
    }

    const curNoteBox = currentNote.getBoundingBox();
    const noteBox = note?.getBoundingBox();

    try {
        if (curNoteBox.getX() !== noteBox.getX()) {
            return false;
        }
    } catch (e) {
        console.log(e);
        return false;
    }

    return true;
};

export const uniqueStringArray = (arr: string[]): string[] => {
    return Array.from(new Set(arr));
};

export const hasDuplicates = (arr: any[]) => {
    return new Set(arr).size !== arr.length;
}

export const getUrlParams = (): URLSearchParams => {
    const params = new URLSearchParams(window.location.search);

    return params;
};

export const getUrlParam = (key: string): string | null => {
    const params = new URLSearchParams(window.location.search);

    return params.get(key);
};

export const noteObjToStr = (note: any) => {
    if (!note) return '';
    const key = note.keys[0].split('/')[0];
    const octave = parseInt(note.keys[0].split('/')[1]);
    const accidentals = note.accidentals[0] ?? '';
    return `${key}${accidentals}${octave}`;
};

export const generateRandomArray = (length: number) => {
    const naturalNumbers = Array.from({ length: length }, (_, i) => i + 1);

    // 打乱数组顺序
    for (let i = naturalNumbers.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [naturalNumbers[i], naturalNumbers[j]] = [naturalNumbers[j], naturalNumbers[i]];
    }

    return naturalNumbers;
};

/**
 * 五线谱相同位置高音、低音区转化层
 * @param note 例如：C4, A5, Bb4, D#2 等等
 * @param type clef_to_base | base_to_clef
 */
export const clefBassTransform = (note: string, type: string | boolean = 'clef_to_bass'): string => {
    if (!type) {
        return note;
    }

    let acc = '';
    let _note: any;
    if (note.length === 3) {
        acc = note[1];
    }

    if (acc) {
        _note = note.split(acc);
        note = `${_note[0]}${_note[1]}`;
    }

    // prettier-ignore
    const clefs = [
        'A6', 'B6', 'C7', 'D7', 'E7', 'F7', 'G7',
        'A5', 'B5', 'C6', 'D6', 'E6', 'F6', 'G6',
        'G5', 'F5', 'E5', 'D5', 'C5', 'B4', 'A4',
        'G4', 'F4', 'E4', 'D4', 'C4', 'B3', 'A3',
        'G3', 'F3', 'E3', 'D3', 'C3', 'B2'
    ]
    // prettier-ignore
    const basses = [
        'C5', 'D5', 'E5', 'F5', 'G5', 'B5', 'A5',
        'C4', 'D4', 'E4', 'F4', 'G4', 'A4', 'B4',
        'B3', 'A3', 'G3', 'F3', 'E3', 'D3', 'C3',
        'B2', 'A2', 'G2', 'F2', 'E2', 'D2', 'C2',
        'B1', 'A1', 'G1', 'F1', 'E1', 'D1'
    ]

    try {
        const index = type === 'clef_to_bass' ? clefs.indexOf(note) : basses.indexOf(note);

        let noteTransformed = type === 'clef_to_bass' ? basses[index] : clefs[index];

        if (acc) {
            noteTransformed = noteTransformed.substring(0, 1) + acc + noteTransformed.substring(1);
        }

        return noteTransformed;
    } catch (e) {
        console.log('高低音区转化失败:', e, note);
        // debug前为了不使页面崩溃
        return 'C4';
    }
};

/**
 *
 * @param note 例如：C4, A5, Bb4, D#2 等等
 * @param key 调号
 */
export const toneTransfer = (note: string, key: string) => {
    // 定义所有调号的音符映射，降号转换为相应的升号
    const keyMap: { [key: string]: { [key: string]: string } } = {
        'C#': { C: 'C#', D: 'D#', E: 'F', F: 'F#', G: 'G#', A: 'A#', B: 'C' },
        D: { C: 'D', D: 'E', E: 'F#', F: 'G', G: 'A', A: 'B', B: 'C#' },
        E: { C: 'E', D: 'F#', E: 'G#', F: 'A', G: 'B', A: 'C#', B: 'D#' },
        'F#': { C: 'F', D: 'G', E: 'A', F: 'A#', G: 'C', A: 'D', B: 'E' },
        G: { C: 'G', D: 'A', E: 'B', F: 'C', G: 'D', A: 'E', B: 'F#' },
        A: { C: 'A', D: 'B', E: 'C#', F: 'D', G: 'E', A: 'F#', B: 'G#' },
        B: { C: 'B', D: 'C#', E: 'D#', F: 'E', G: 'F#', A: 'G#', B: 'A#' },
        Db: { C: 'C#', D: 'D#', E: 'E', F: 'F#', G: 'G#', A: 'A#', B: 'B' },
        Eb: { C: 'D#', D: 'F', E: 'F#', F: 'G#', G: 'A#', A: 'C', B: 'D' },
        F: { C: 'F', D: 'G', E: 'A', F: 'A#', G: 'C', A: 'D', B: 'E' },
        Gb: { C: 'F#', D: 'G#', E: 'A#', F: 'B', G: 'C#', A: 'D#', B: 'E' },
        Ab: { C: 'G#', D: 'A#', E: 'B', F: 'C#', G: 'D#', A: 'E', B: 'F#' },
        Bb: { C: 'A#', D: 'B', E: 'C', F: 'D#', G: 'E', A: 'F#', B: 'G#' },
        Cb: { C: 'B', D: 'C#', E: 'D#', F: 'E', G: 'F#', A: 'G#', B: 'A#' },
    };
    if (key === 'C' || key === 'none' || !key) {
        return note;
    }

    // 检查输入的音符是否在映射表中
    if (keyMap[key].hasOwnProperty(note)) {
        return equalNote(keyMap[key][note]);
    }
    return equalNote(note);
};

export const equalNote = (note: string) => {
    switch (note) {
        case 'C#':
            return 'Db';
        case 'D#':
            return 'Eb';
        case 'F#':
            return 'Gb';
        case 'G#':
            return 'Ab';
        case 'A#':
            return 'Bb';
        default:
            return note;
    }
};

export const noteEnharmonicByAcc = (note: string, accNotes: any, charIndex: number, n: number) => {
    if (accNotes.length > 0) {
        const firstAccBarIndex = accNotes[0].barIndex
        const firstAccNoteIndex = accNotes[0].noteIndex
        accNotes.forEach((accNote: accNotesType) => {
            if (firstAccBarIndex < charIndex || (firstAccBarIndex === charIndex && firstAccNoteIndex <= n)) {
                // 只影响音区内的和弦
                if (Note.enharmonic(Note.pitchClass(note)) === (accNote.letter + accNote.acc)) {
                    note = Note.enharmonic(note);
                }
            }
        });
    }
    return note;
}

export const noteMatchAcc = (note: string, accNotes: any) => {
    const noteObj = Note.get(note);
    if (!noteObj.acc) {
        return true;
    }
    let t = false;
    accNotes.forEach((accNote: any) => {
        if (accNote.letter === noteObj.letter && accNote.acc === noteObj.acc) {
            t = true;
        }
    });
    return t;
}

export const removeEmptyArrays = (obj: any) => {
    // 获取对象的所有键
    Object.keys(obj).forEach(key => {
        // 检查键对应的值是否为数组且为空
        if (Array.isArray(obj[key]) && obj[key].length === 0) {
            // 删除该键
            delete obj[key];
        }
    });
    return obj;
}

export const findPreviousIndexes = (charIndex: number, noteIndex: number) => {
    if (noteIndex >= 1) {
        return { previousCharIndex: charIndex, previousNoteIndex: noteIndex - 1 }
    }
    if (noteIndex === 0) {
        if (charIndex >= 1) {
            return { previousCharIndex: charIndex - 1, previousNoteIndex: 3 }
        } else {
            return { previousCharIndex: -1, previousNoteIndex: -1 }
        }
    }
    return { previousCharIndex: -1, previousNoteIndex: -1 }
}

export const findNextIndexes = (charIndex: number, noteIndex: number) => {
    if (noteIndex < 3) {
        return { nextCharIndex: charIndex, nextNoteIndex: noteIndex + 1 }
    } else {
        return { nextCharIndex: charIndex + 1, nextNoteIndex: 0 }
    }
}

export const shuffleArray = (array: any[]) => {
    // 复制原数组，避免直接修改
    const result = array.slice();
    let currentIndex = result.length, temporaryValue, randomIndex;

    // 当前索引从数组最后一个元素开始向前遍历
    while (currentIndex !== 0) {
        // 选取一个从开始到当前索引的随机索引
        randomIndex = Math.floor(Math.random() * currentIndex);
        currentIndex -= 1;

        // 交换当前元素与随机选取的元素
        temporaryValue = result[currentIndex];
        result[currentIndex] = result[randomIndex];
        result[randomIndex] = temporaryValue;
    }

    return result;
}

export const _simplifyRelation = (note1: string, note2: string) => {

    const t = (note1: string, note2: string) => {
        const interval = Interval.get(distance(note1, note2));
        return Interval.simplify(Interval.fromSemitones(interval.semitones || 0))
    }

    let relation = t(note1, note2);
    const semiton = parseInt(Interval.fromSemitones(Interval.get(distance(note1, note2)).semitones || 0));
    if (semiton > 8) {
        const noteTransposed = transpose(note1, `${Math.floor(semiton / 8) * 7 + 1}P`)
        relation = distance(noteTransposed, note2);
    }
    return relation;
}

export const simplifyRelation = (note1: string, note2: string) => {
    let relation = distance(note1, note2);
    const semiton = parseInt(Interval.fromSemitones(Interval.get(distance(note1, note2)).semitones || 0));
    if (semiton > 8) {
        const noteTransposed = transpose(note1, `${Math.floor(semiton / 8) * 7 + 1}P`)
        relation = distance(noteTransposed, note2);
    }
    return relation;
}

export const mapNumberToInterval = (num: number) => {
    switch (num) {
        case 0:
            return '1P';
        case 1:
            return '2m';
        case 2:
            return '2M';
        case 3:
            return '3m';
        case 4:
            return '3M';
        case 5:
            return '4P';
        case 6:
            return '5d';
        case 7:
            return '5P';
        case 8:
            return '6m';
        case 9:
            return '6M';
        case 10:
            return '7m';
        case 11:
            return '7M';
        case 12:
            return '8P';
    }
}

export const arrEqualIgnoreOrder = (arr1: any[], arr2: any[]) => {
    if (arr1.length !== arr2.length) {
        return false;
    }
    // 对数组进行排序
    const sortedArr1 = arr1.slice().sort().map(v => v.replace('/', ''));
    const sortedArr2 = arr2.slice().sort().map(v => v.replace('/', ''));
    // 比较排序后的数组
    for (let i = 0; i < sortedArr1.length; i++) {
        const midi_1 = Note.midi(sortedArr1[i]);
        const midi_2 = Note.midi(sortedArr2[i]);
        if (midi_1 !== midi_2) {
            // 判断音符相等考虑等音问题
            return false;
        }
    }
    return true;
}
